import React from "react";
import {
    PrintTemplate
} from "crqlar-react-common/dist/models/template/printTemplate.model";
import {
    CalculatedAgendaItemResponseDTO,
} from "crqlar-react-common/dist/models/agenda/agenda.model";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";

interface Props {
    activeTemplate: PrintTemplate | null
    primaryLanguage: string
    dateRange: { start: Date, end: Date }
    secondaryLanguage: string | null
    items: CalculatedAgendaItemResponseDTO[]
}

const getTranslation = (template: PrintTemplate | null, key: string, lang: string): string | null => {
    const localizedContent = template?.[key as keyof PrintTemplate] as Record<string, string> | undefined;
    return localizedContent?.[lang] || null;
};

const AgendaPreviewContent: React.FC<Props> = ({
                                                   activeTemplate,
                                                   primaryLanguage,
                                                   dateRange,
                                                   secondaryLanguage,
                                                   items,
                                               }) => {
    const headerImage = activeTemplate?.headerLogo?.mediaURI ?? null;
    const primaryLocale = primaryLanguage === 'de' ? 'de-DE' : 'en-US';
    const dateOptions: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    };

    // Group items by date - no filtering needed, backend already handles it
    const itemsByDate = items.reduce((acc, item) => {
        const dateStr = new Date(item.startTime).toLocaleDateString(primaryLocale, dateOptions);
        if (!acc[dateStr]) acc[dateStr] = [];
        acc[dateStr].push(item);
        return acc;
    }, {} as Record<string, CalculatedAgendaItemResponseDTO[]>);

    const renderLogo = () => {
        if (!headerImage) return null;
        return (
            <div style={{
                width: "100%",
                maxHeight: "250px",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}>
                <img
                    src={headerImage}
                    alt="Logo"
                    crossOrigin="anonymous"
                    style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "cover",
                        objectPosition: "center center",
                        display: "block",
                    }}
                />
            </div>
        );
    };

    const renderHeader = () => {
        const primaryTitle = getTranslation(activeTemplate, "headerContent1Localized", primaryLanguage);
        const secondaryTitle = secondaryLanguage ? getTranslation(activeTemplate, "headerContent1Localized", secondaryLanguage) : null;
        if (!primaryTitle) return null;

        return (
            <div>
                <div style={{
                    fontWeight: '300',
                    fontSize: '16pt',
                    color: '#1a1a1a',
                    textAlign: 'center',
                    letterSpacing: '0.5px'
                }}>
                    {primaryTitle}
                </div>
                {secondaryTitle && (
                    <div style={{
                        fontWeight: '300',
                        fontSize: '11pt',
                        color: '#999',
                        textAlign: 'center',
                        letterSpacing: '0.3px',
                        marginTop: '2px'
                    }}>
                        {secondaryTitle}
                    </div>
                )}
            </div>
        );
    };

    const renderSubheader = () => {
        const primarySubtitle = getTranslation(activeTemplate, "headerContent2Localized", primaryLanguage);
        const secondarySubtitle = secondaryLanguage ? getTranslation(activeTemplate, "headerContent2Localized", secondaryLanguage) : null;
        if (!primarySubtitle) return null;

        return (
            <div>
                <div style={{
                    fontSize: '9pt',
                    color: '#666',
                    textAlign: 'center',
                    fontWeight: '300',
                    fontStyle: 'italic'
                }}>
                    {primarySubtitle}
                </div>
                {secondarySubtitle && (
                    <div style={{
                        fontSize: '8pt',
                        color: '#999',
                        textAlign: 'center',
                        fontWeight: '300',
                        fontStyle: 'italic',
                        marginTop: '1px'
                    }}>
                        {secondarySubtitle}
                    </div>
                )}
            </div>
        );
    };

    const renderTime = () => {
        const startPrimary = new Date(dateRange.start).toLocaleDateString(primaryLocale, dateOptions);
        const endPrimary = new Date(dateRange.end).toLocaleDateString(primaryLocale, dateOptions);

        if (!secondaryLanguage) {
            return (
                <div style={{
                    fontSize: '10pt',
                    color: '#666',
                    textAlign: 'center',
                    fontWeight: '300'
                }}>
                    {startPrimary} - {endPrimary}
                </div>
            );
        }

        const secondaryLocale = secondaryLanguage === 'de' ? 'de-DE' : 'en-US';
        const startSecondary = new Date(dateRange.start).toLocaleDateString(secondaryLocale, dateOptions);
        const endSecondary = new Date(dateRange.end).toLocaleDateString(secondaryLocale, dateOptions);

        return (
            <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '10pt', color: '#666', fontWeight: '300'}}>
                    {startPrimary} - {endPrimary}
                </div>
                <div style={{
                    fontSize: '8pt',
                    color: '#999',
                    fontWeight: '300',
                    marginTop: '1px'
                }}>
                    {startSecondary} - {endSecondary}
                </div>
            </div>
        );
    };

    const renderFooter = () => {
        const primaryFooter1 = getTranslation(activeTemplate, "bottomContent1Localized", primaryLanguage);
        const secondaryFooter1 = secondaryLanguage ? getTranslation(activeTemplate, "bottomContent1Localized", secondaryLanguage) : null;
        const primaryFooter2 = getTranslation(activeTemplate, "bottomContent2Localized", primaryLanguage);
        const secondaryFooter2 = secondaryLanguage ? getTranslation(activeTemplate, "bottomContent2Localized", secondaryLanguage) : null;

        if (!primaryFooter1 && !primaryFooter2) return null;

        return (
            <div style={{
                marginTop: '8mm',
                paddingTop: '4mm',
                borderTop: '1px solid #ddd'
            }}>
                {primaryFooter1 && (
                    <div>
                        <div style={{
                            fontSize: '8pt',
                            color: '#666',
                            textAlign: 'center',
                            fontWeight: '300'
                        }}>
                            {primaryFooter1}
                        </div>
                        {secondaryFooter1 && (
                            <div style={{
                                fontSize: '7pt',
                                color: '#999',
                                textAlign: 'center',
                                fontWeight: '300',
                                marginTop: '1px'
                            }}>
                                {secondaryFooter1}
                            </div>
                        )}
                    </div>
                )}
                {primaryFooter2 && (
                    <div style={{marginTop: primaryFooter1 ? '3mm' : 0}}>
                        <div style={{
                            fontSize: '8pt',
                            color: '#666',
                            textAlign: 'center',
                            fontWeight: '300'
                        }}>
                            {primaryFooter2}
                        </div>
                        {secondaryFooter2 && (
                            <div style={{
                                fontSize: '7pt',
                                color: '#999',
                                textAlign: 'center',
                                fontWeight: '300',
                                marginTop: '1px'
                            }}>
                                {secondaryFooter2}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderAgendaItems = () => {
        if (!items || items.length === 0) {
            return (
                <div style={{textAlign: 'center', color: '#999', marginTop: '8mm'}}>
                    No items
                </div>
            );
        }

        return (
            <div style={{width: '100%', marginTop: '6mm'}}>
                {Object.entries(itemsByDate).map(([dateStr, dateItems], dateIndex) => (
                    <React.Fragment key={dateStr}>
                        <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#222',
                            marginTop: dateIndex > 0 ? '16px' : 0,
                            marginBottom: '12px'
                        }}>
                            {dateStr}
                        </div>

                        {dateItems.map((item, itemIndex) => {
                            const name = item.service.nameLocalized?.[primaryLanguage] || item.service.name || "Event";
                            const description = item.service.descriptionLocalized?.[primaryLanguage] ?? item.service.shortDescriptionLocalized?.[primaryLanguage];
                            const secondaryName = secondaryLanguage ? item.service.nameLocalized?.[secondaryLanguage] : null;
                            const secondaryDescription = secondaryLanguage ? (item.service.descriptionLocalized?.[secondaryLanguage] ?? item.service.shortDescriptionLocalized?.[secondaryLanguage]) : null;
                            const key = `${item.service?.id ?? ''}-${item.startTime}`;
                            const showBottomBorder = dateItems.length > 1 && itemIndex < dateItems.length - 1;

                            return (
                                <div
                                    key={key}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        padding: '8px 0',
                                        gap: '12px',
                                        ...(showBottomBorder && {borderBottom: '1px dashed #eee'}),
                                        ...(itemIndex === 0 && {
                                            borderTop: '2px solid #ddd',
                                            paddingTop: '12px'
                                        })
                                    }}
                                >
                                    <div style={{flex: 1}}>
                                        <div style={{
                                            fontSize: '11pt',
                                            fontWeight: 600,
                                            color: '#111',
                                            marginBottom: secondaryName ? '2px' : '4px'
                                        }}>
                                            {name}
                                        </div>
                                        {secondaryName && (
                                            <div style={{
                                                fontSize: '9pt',
                                                fontWeight: 600,
                                                color: '#999',
                                                marginBottom: '4px'
                                            }}>
                                                {secondaryName}
                                            </div>
                                        )}
                                        {description && (
                                            <div style={{
                                                fontSize: '9pt',
                                                color: '#666',
                                                lineHeight: 1.3,
                                                marginBottom: secondaryDescription ? '2px' : 0
                                            }}>
                                                {description}
                                            </div>
                                        )}
                                        {secondaryDescription && (
                                            <div style={{
                                                fontSize: '8pt',
                                                color: '#999',
                                                lineHeight: 1.3
                                            }}>
                                                {secondaryDescription}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{
                                        width: '120px',
                                        minWidth: '90px',
                                        textAlign: 'right',
                                        color: '#222',
                                        fontSize: '10pt'
                                    }}>
                                        {formatDateHM(item.startTime)} - {formatDateHM(item.endTime)}
                                    </div>
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    return (
        <div>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                marginBottom: '8mm',
            }}>
                {renderLogo()}
                {renderHeader()}
                {renderSubheader()}
                {renderTime()}
            </div>
            {renderAgendaItems()}
            {renderFooter()}
        </div>
    );
};

export default AgendaPreviewContent;